const path = require('path');

const contentModel = require('../helpers/contentfulContentModel');
const moduleUtils = require('../../utils/contentfulModules');
const { SECURE_PREVIEW_DIRECTORY } = require('../../utils/preview');
const { stripHtmlOrNull } = require('../../utils/formatting.cjs');
const {
  storeFrontProgramUUIDs,
} = require('../../source-utils/constants');

const query = async (graphql, limitedProgramUUIDs) => graphql(`
 {
    allProgram
      (filter: {
        inProspectus: {eq: true},
        type: {nin: "Masters"},
        ${limitedProgramUUIDs !== undefined ? `, uuid: {in: ${JSON.stringify(limitedProgramUUIDs)}}` : ''}
      })
    {
      edges {
        node {
          updatedAt: dataModifiedTimestamp,
          applicableSeatTypes
          authoringOrganizations {
            uuid
            key
            logoImageUrl
            name
            marketingUrl
            description
            certificateLogoImageUrl
          }
          instructorOrdering {
            uuid
            givenName
            familyName
            bio
            slug
            profileImageUrl
            position {
              title
              organizationName
            }
          }
          corporateEndorsements {
            corporationName
            image {
              width
              src
            }
            individualEndorsements {
              endorser {
                givenName
                familyName
                position {
                  title
                }
              }
              quote
            }
            statement
          }
          courses {
            course {
              uuid
              title
              subtitle: shortDescription
              levelType
              marketingUrl
              inProspectus
              prospectusPath
              entitlements {
                mode
                price
                currency
                sku
              }
              courseRunKeys
              activeCourseRun {
                key
                uuid
                title
                isEnrollable
                shortDescription
                marketingPath 
                marketingUrl
                start
                end
                pacingType
                type
                status
                course
                contentLanguage
                minEffort
                maxEffort
                weeksToComplete
                levelType
                availability
                seats {
                  type
                  price
                  currency
                  upgradeDeadline
                  sku
                  bulkSku
                  creditProvider
                }
                staff {
                  uuid
                  givenName
                  familyName
                  slug
                  profileImageUrl
                  position {
                    title
                    organizationName
                  }
                }
              }
              topics {
                topic
              }
            }
          }
          cardImageUrl
          creditRedemptionOverview
          creditValue
          enrollBaseUrl
          expectedLearningItems
          hasOfacRestrictions
          faq {
            question
            answer
          }
          isProgramEligibleForOneClickPurchase
          inYearValue {
              perLeadUsa
              perLeadInternational
              perClickUsa
              perClickInternational
          }
          jobOutlookItems
          locationRestriction {
            restrictionType
            countries
            states
          }
          marketingSlug
          marketingUrl
          marketingHook
          subscriptionEligible
          subscriptionPrices {
            price
            currency
          }
          overview
          programDurationOverride
          programOriginalPrice
          programPrice
          skus
          staff {
            uuid
            givenName
            familyName
            slug
            profileImageUrl
            position {
              title
              organizationName
            }
          }
          status
          subtitle
          subscriptionEligible
          title
          type
          uuid
          video {
            src
          }
          subjects {
            uuid
            name
            slug
            subject {
              labels {
                es
                en
              }
            }
          }
        }
      }
    }

    allCurrency {
      edges {
        node {
          ISO3
          currencyInfo {
            code
            rate
            symbol
          }
        }
      }
    }
    allContentfulCatalogPageCustomization(filter: {pageType: {eq: "program"}}) {
      edges {
        node {
          ${contentModel.catalogPageCustomization}
        }
      }
    }
  }
`);

const discoveryCardFields = `
  uuid
  title
  subtitle
  overview
  cardImageUrl
  authoringOrganizations {
    key
    logoImageUrl
    name
  }
  courses {
    course {
      title
      topics {
        topic
      }
    }
  }
  type
  marketingPath
  organizationLogoOverrideUrl
  organizationShortCodeOverride
  productSource {
    name
    slug
    description
  }
  status
  hidden
  inProspectus
  is2UDegreeProgram
  degree {
    quickFacts {
      text
      icon
    }
    additionalMetadata {
      externalIdentifier
      externalUrl
      organicUrl
    }
    organizationLogoOverrideUrl
  }
  locationRestriction {
    restrictionType
    countries
    states
  }
  subscriptionEligible
  levelTypeOverride {
    name
  }
  courseCount
  weeksToComplete
  weeksToCompleteMax
`;

// -----------------------------------------------------------------------------
// This code is for the MIT Statistics and Data Science track selection work.
// It is a temporary solution to a problem we hope to solve more robustly in the
// future. For now, handle the MIT SDS program page differently than other
// program pages by retrieving and passing the data of its linked program also.
//
//    * MIT_SDS_PROGRAM_1_UUID: The original MIT SDS program; this is the page
//      that will be built differently
//    * MIT_SDS_PROGRAM_2_UUID: The additonal program that has the data we want
//      to pass to PROGRAM_1 as the "linkedProgram"
//
// -----------------------------------------------------------------------------
const MIT_SDS_PROGRAM_1_UUID = '71c95834-f6df-4f78-8cd7-c6461dd9b1d4';
const MIT_SDS_PROGRAM_2_UUID = 'fa06b9c5-fe2b-41d8-a8c1-1bfd9c0d7b07';
const createProgramWithTracks = (result, node, createPage, customSeoFields, customMetaTags, basePath, retiringSoon) => {
  const linkedProgram = result.data.allProgram.edges.find(edge => edge.node.uuid === MIT_SDS_PROGRAM_2_UUID).node;
  const strippedSubtitle = stripHtmlOrNull(node.subtitle);

  const programPath = node.marketingSlug.includes('/') ? node.marketingSlug : `${basePath}/${node.marketingSlug}`;
  const programContext = {
    updatedAt: node.updatedAt,
    program: {
      ...node,
      retiringSoon,
      strippedSubtitle,
    },
    allCurrency: result.data.allCurrency,
    linkedProgram,
    customMetaTags,
    customSeoFields,
  };

  let isStorefront = false;

  if (storeFrontProgramUUIDs.includes(node.uuid)) {
    isStorefront = true;
  }

  createPage({
    path: programPath,
    component: path.resolve('./src/packages/program-page/template/ProgramDetailPage/index.jsx'),
    context: {
      ...programContext,
    },
  });

  // webview routes
  createPage({
    path: `webview/${programPath}`,
    component: path.resolve('./src/packages/program-page/template/ProgramDetailPage/index.jsx'),
    context: {
      ...programContext,
      webview: true,
    },
  });

  if (isStorefront) {
    const storefrontPath = '/secure-preview/storefront/';
    const sfProgramPath = node.marketingSlug.includes('/')
      ? `${storefrontPath}${node.marketingSlug}`
      : `${storefrontPath}${basePath}/${node.marketingSlug}`;
    console.log(`Creating secure storefront program page for ${sfProgramPath}`);

    createPage({
      path: sfProgramPath,
      component: path.resolve('./src/packages/storefront/product-pages/pages/program-page/index.tsx'),
      context: {
        ...programContext,
      },
    });

    createPage({
      path: `webview${sfProgramPath}`,
      component: path.resolve('./src/packages/storefront/product-pages/pages/program-page/index.tsx'),
      context: {
        ...programContext,
        webview: true,
      },
    });
  }
};

const createProgram = (result, createPage) => {
  const allPageCustomizations = moduleUtils.extractNodes(result, 'allContentfulCatalogPageCustomization');

  result.data.allProgram.edges.forEach(({ node }) => {
    const pageCustomizations = allPageCustomizations.find(config => config.uuid === node.uuid);
    const hasCustomSeoFields = pageCustomizations && pageCustomizations.customSeoFields
    && pageCustomizations.customSeoFields.length > 0;
    const customSeoFields = hasCustomSeoFields ? pageCustomizations.customSeoFields : [];
    const hasCustomMetaTags = pageCustomizations && pageCustomizations.customMetaTags
    && pageCustomizations.customMetaTags.length > 0;
    const customMetaTags = hasCustomMetaTags ? pageCustomizations.customMetaTags : [];
    const retiringSoon = pageCustomizations && !!pageCustomizations.retiringSoon;

    const isActiveProgram = node.status.toLowerCase() === 'active';
    const isUnpublishedProgram = node.status.toLowerCase() === 'unpublished';

    if (!isActiveProgram && !isUnpublishedProgram) {
      return;
    }

    if (node.courses.length === 0) {
      // eslint-disable-next-line no-console
      console.info(`Program with uuid ${node.uuid} wasn't built due to missing courses`);
      return;
    }

    const pathPrefix = isUnpublishedProgram ? `${SECURE_PREVIEW_DIRECTORY}/` : '';
    const basePath = `${pathPrefix}${node.type.toLowerCase().replace(/\s+/g, '-')}`;

    // For program track selection; see comment above.
    if (node.uuid === MIT_SDS_PROGRAM_1_UUID) {
      createProgramWithTracks(result, node, createPage, customSeoFields, customMetaTags, basePath, retiringSoon);
      return;
    }
    const strippedSubtitle = stripHtmlOrNull(node.subtitle);

    const programContext = {
      updatedAt: node.updatedAt,
      program: {
        ...node,
        retiringSoon,
        strippedSubtitle,
      },
      allCurrency: result.data.allCurrency,
      customSeoFields,
      customMetaTags,
    };
    const programPath = node.marketingSlug.includes('/')
      ? `${pathPrefix}${node.marketingSlug}`
      : `${basePath}/${node.marketingSlug}`;

    let isStorefront = false;

    if (storeFrontProgramUUIDs.includes(node.uuid)) {
      isStorefront = true;
    }

    createPage({
      path: programPath,
      component: path.resolve('./src/packages/program-page/template/ProgramDetailPage/index.jsx'),
      context: {
        ...programContext,
      },
    });

    // webview routes
    createPage({
      path: `webview/${programPath}`,
      component: path.resolve('./src/packages/program-page/template/ProgramDetailPage/index.jsx'),
      context: {
        ...programContext,
        webview: true,
      },
    });

    if (isStorefront) {
      const storefrontPath = '/secure-preview/storefront/';
      const sfProgramPath = node.marketingSlug.includes('/')
        ? node.marketingSlug
        : `${basePath}/${node.marketingSlug}`;
      console.log(`Creating secure storefront program page for ${storefrontPath}${sfProgramPath}`);

      createPage({
        path: `${storefrontPath}${sfProgramPath}`,
        component: path.resolve('./src/packages/storefront/product-pages/pages/program-page/index.tsx'),
        context: {
          ...programContext,
        },
      });

      createPage({
        path: `${storefrontPath}webview${sfProgramPath}`,
        component: path.resolve('./src/packages/storefront/product-pages/pages/program-page/index.tsx'),
        context: {
          ...programContext,
          webview: true,
        },
      });
    }
  });
};

module.exports = {
  createProgram,
  query,
  discoveryCardFields,
  MIT_SDS_PROGRAM_1_UUID,
  MIT_SDS_PROGRAM_2_UUID,
};
